import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, MenuItem, TextField, Unstable_Grid2 as Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RestartAlt as ResetIcon } from '@mui/icons-material';
import DynamicPerformanceDataGrid from 'components/performance/DynamicPerformanceDataGrid';
import PlayResultsList from 'components/performance/PlayResultsList';
import PillTabs from 'components/shared/tabs/PillTabs';
import DynamicPerformanceModal from 'components/performance/DynamicPerformanceModal';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import StyledFiltersContainer from 'common/theme/filters/StyledFiltersContainer';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import performanceSlice from 'store/modules/performance';
import requestStatus from 'common/constants/status';

function PerformanceList() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes', 'performances']);
  const pageTitle = t('performance.list', { ns: 'routes' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const dynamicPerformances = useSelector((state) => state.performance.data.dynamicPerformances);
  const refresh = useSelector((state) => state.performance.refresh);
  const updateLoading = useSelector((state) => state.performance.status.updateDynamic === requestStatus.loading);
  const updateSuccess = useSelector((state) => state.performance.status.updateDynamic === requestStatus.success);
  const dynamicStatus = useSelector((state) => state.performance.status.listDynamic);
  const clients = useSelector((state) => state.client?.data?.all || []);
  const exercises = useSelector((state) => state.exercise?.data?.exercises || []);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDynamic, setSelectedDynamic] = useState(null);
  const [nameFilter, setNameFilter] = useState('');
  const [exerciseFilter, setExerciseFilter] = useState('all');
  // Dos fuentes distintas de marcas: las que registra un entrenador (OTRIXVERSE) y las que
  // auto-reporta el cliente en Play. Antes solo se veían las primeras y los clientes de Play
  // parecían no tener rendimientos.
  const [source, setSource] = useState(0);
  const isPlayTab = source === 1;

  // Filtros client-side: nombre de cliente + ejercicio (de los presentes en la lista cargada).
  const exerciseOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    dynamicPerformances.forEach((p) => { const n = p.exercise?.name; if (n && !seen.has(n)) { seen.add(n); opts.push(n); } });
    return opts.sort();
  }, [dynamicPerformances]);

  const filteredPerformances = useMemo(() => dynamicPerformances.filter((p) => {
    if (exerciseFilter !== 'all' && p.exercise?.name !== exerciseFilter) return false;
    if (nameFilter.trim()) {
      const q = nameFilter.toLowerCase().trim();
      const full = `${p.client?.first_name || ''} ${p.client?.last_name || ''}`.toLowerCase();
      if (!full.includes(q)) return false;
    }
    return true;
  }), [dynamicPerformances, nameFilter, exerciseFilter]);

  const handleDynamicEdit = (performance) => {
    setSelectedDynamic(performance);
    setEditModalOpen(true);
  };

  const handleDynamicUpdate = (data) => {
    if (selectedDynamic) {
      dispatch(performanceSlice.actions.updateDynamic({ id: selectedDynamic.id, input: data }));
    }
  };

  const handleDynamicDeleteClick = (performance) => {
    setSelectedDynamic(performance);
    setDeleteDialogOpen(true);
  };

  const handleDynamicDeleteConfirm = () => {
    if (selectedDynamic) {
      dispatch(performanceSlice.actions.deleteDynamic(selectedDynamic.id));
      setDeleteDialogOpen(false);
      setSelectedDynamic(null);
    }
  };

  useEffect(() => {
    if (updateSuccess) {
      setEditModalOpen(false);
      setSelectedDynamic(null);
    }
  }, [updateSuccess]);

  useEffect(() => {
    dispatch(performanceSlice.actions.listDynamic({}));
  }, [refresh, dispatch]);

  useEffect(() => {
    setAppBarButton(
      <Button
        component={RouterLink}
        to={routes.createPerformanceDynamic.path}
        variant="contained"
        startIcon={<AddIcon />}
        sx={{ fontWeight: 600, borderRadius: 2 }}
      >
        {t('add', { ns: 'buttons' })}
      </Button>
    );
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle, t]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />

      <PillTabs
        value={source}
        onChange={(_, v) => {
          setSource(v);
          // El desplegable de ejercicios se arma con los de la pestaña activa: al cambiar de
          // fuente el valor anterior ya no existe y dejaría la lista vacía.
          setExerciseFilter('all');
        }}
        ariaLabel={pageTitle}
        sx={{ mb: 2 }}
        tabs={[
          { key: 'otrixverse', label: t('sources.otrixverse', { ns: 'performances', defaultValue: 'OTRIXVERSE' }) },
          { key: 'play', label: t('sources.play', { ns: 'performances', defaultValue: 'OTRIX Play' }) }
        ]}
      />

      <StyledFiltersContainer>
        <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="center">
          <Grid xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              size="small"
              label={t('client', { ns: 'labels', defaultValue: 'Cliente' })}
              placeholder={t('searchClient', { ns: 'sessions', defaultValue: 'Buscar cliente' })}
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </Grid>
          <Grid xs={12} sm={6} md={5}>
            <TextField select fullWidth size="small" label={t('exercise', { ns: 'labels', defaultValue: 'Ejercicio' })} value={exerciseFilter} onChange={(e) => setExerciseFilter(e.target.value)}>
              <MenuItem value="all">{t('allExercises', { ns: 'labels', defaultValue: 'Todos los ejercicios' })}</MenuItem>
              {exerciseOptions.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid xs={12} md={2}>
            <Button fullWidth variant="text" color="inherit" startIcon={<ResetIcon />} onClick={() => { setNameFilter(''); setExerciseFilter('all'); }} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700 }}>
              {t('reset', { ns: 'buttons', defaultValue: 'Reiniciar' })}
            </Button>
          </Grid>
        </Grid>
      </StyledFiltersContainer>

      <Box sx={{ position: 'relative', mt: 1 }}>
        {isPlayTab ? (
          <PlayResultsList nameFilter={nameFilter} exerciseFilter={exerciseFilter} />
        ) : (
          <DynamicPerformanceDataGrid
            performances={filteredPerformances}
            onEdit={handleDynamicEdit}
            onDelete={handleDynamicDeleteClick}
            loading={dynamicStatus === 'loading'}
          />
        )}
      </Box>

      {/* Edit Dynamic Performance Modal */}
      <DynamicPerformanceModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDynamic(null);
        }}
        onSubmit={handleDynamicUpdate}
        performance={selectedDynamic}
        clients={clients}
        exercises={exercises}
        loading={updateLoading}
      />

      {/* Eliminar rendimiento (modal de confirmación compartido). */}
      <ConfirmDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDynamicDeleteConfirm}
        title={t('dynamicPerformances.confirmDelete', { ns: 'performances', defaultValue: 'Eliminar Rendimiento' })}
        message={t('dynamicPerformances.confirmDeleteMessage', { ns: 'performances', defaultValue: '¿Estás seguro de que deseas eliminar este rendimiento?' })}
      />
    </>
  );
}

export default PerformanceList;
